import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import Confirm from 'components/shared/Modal/Confirm/Confirm';

import useNavigationBlocker from '../../hooks/useNavigationBlocker';

import DirtyTrackerContext from './DirtyTrackerContext';
import { useConfirmPreference } from './lib/useConfirmPreference';

/**
 * @param {import('./lib/propTypes').DirtyTrackerProps}
 */

const DirtyTracker = ({
  isDirty,
  confirmKey,
  color = 'danger',
  message = 'You have unsaved changes. Are you sure you want to leave?',
  handleContinue,
  onAutoSave,
  footerActions,
  children
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const shouldEnablePreference = !!confirmKey;
  const { shouldSkipConfirm, handlePreferencePersist } = useConfirmPreference({ confirmKey });

  const { showConfirm, handleConfirm, handleCancel, triggerConfirm, getPendingType } =
    useNavigationBlocker(isDirty && !shouldSkipConfirm, message);

  const isAutoSaving = !!onAutoSave && showConfirm && getPendingType() !== 'refresh';

  useLayoutEffect(() => {
    if (isAutoSaving) {
      onAutoSave();
      handleConfirm();
    }
  });

  const [frozenMessage, setFrozenMessage] = useState(message);
  const wasOpen = useRef(false);

  useLayoutEffect(() => {
    if (showConfirm && !wasOpen.current) setFrozenMessage(message);
    wasOpen.current = showConfirm;
  }, [showConfirm, message]);

  useEffect(() => {
    if (!showConfirm && dontShowAgain) {
      setDontShowAgain(false);
    }
  }, [dontShowAgain, showConfirm]);

  const handleConfirmWithPreference = () => {
    if (dontShowAgain) {
      handlePreferencePersist(true);
    }
    setDontShowAgain(false);
    handleConfirm();
    handleContinue?.();
  };

  const handleCancelWithReset = () => {
    setDontShowAgain(false);
    handleCancel();
  };

  /**
   * Object: primary onClick is forced to confirm. Function `(handleContinue, handleClose)`:
   * consumer wires the handlers (continue = leave, close = stay) and the config is used as-is.
   */
  const resolvedFooterActions =
    typeof footerActions === 'function'
      ? footerActions(handleConfirmWithPreference, handleCancelWithReset)
      : {
          ...footerActions,
          primary: {
            text: 'Confirm',
            ...footerActions?.primary,
            onClick: handleConfirmWithPreference
          }
        };

  const contextValue = useMemo(
    () => ({
      triggerConfirm,
      /**
       * Wrapper for triggerConfirm with a more intuitive name
       * @param {Function} action - The action to execute if user confirms or if no changes
       * @param {boolean} [forceCheck] - Optional override for isDirty state
       */
      handleProtectedAction: (action, forceCheck) => {
        const shouldCheck = forceCheck !== undefined ? forceCheck : isDirty;
        triggerConfirm(action, shouldCheck && !shouldSkipConfirm);
      }
    }),
    [triggerConfirm, isDirty, shouldSkipConfirm]
  );

  return (
    <DirtyTrackerContext.Provider value={contextValue}>
      {children}
      <Confirm
        type="unsaved-changes"
        overlayClassName="z-[110]"
        variant={color}
        message={frozenMessage}
        isOpen={showConfirm && !isAutoSaving}
        handleClose={handleCancelWithReset}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={setDontShowAgain}
        showDontShowThisMessageAgain={shouldEnablePreference}
        footerActions={resolvedFooterActions}
      />
    </DirtyTrackerContext.Provider>
  );
};

export default DirtyTracker;
